/**
 * Onboarding progress ring (docs/ONBOARDING_DESIGN_RULES.md F2, F3, M4).
 *
 * The server renders the ring at its current value (templates/macros/
 * onboarding.html, onboard_ring). This controller keeps it in step when the
 * done value changes on the page, so the fill moves the moment an item
 * ticks; the fill and the closing check are CSS transitions in
 * static/css/onboarding.css.
 *
 * A ring with a memory value also remembers what it last showed, on this
 * device, under that name. Rendered further along than that, it starts from
 * the old value and fills to the new one: an item ticked on another page, or
 * by the client accepting a quote, is seen moving the next time the ring is.
 * The dashboard's checklist and the navigation ring share one memory, so a
 * tick plays once wherever it is next seen.
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['fill', 'count'];
    static values = { done: Number, total: Number, memory: String };

    initialize() {
        // Read before the first doneValueChanged, which runs ahead of
        // connect() and stores the new value.
        this._before = this._recall();
    }

    connect() {
        const before = this._before;
        this._before = null;
        if (before !== null && before < this.doneValue) {
            this.element.setAttribute('data-instant', '');
            this._render(before);
            void this.element.getBoundingClientRect();
            this._frame = requestAnimationFrame(() => {
                this.element.removeAttribute('data-instant');
                this._render(this.doneValue);
            });
        }
        this._remember();
    }

    disconnect() {
        cancelAnimationFrame(this._frame);
    }

    doneValueChanged() {
        this._render(this.doneValue);
        this._remember();
    }

    totalValueChanged() {
        this._render(this.doneValue);
    }

    _render(value) {
        if (!this.hasFillTarget) return;
        const total = this.totalValue;
        const done = Math.min(Math.max(value, 0), total);
        const share = total > 0 ? done / total : 0;
        this.fillTarget.style.strokeDashoffset = String(100 - share * 100);
        this.element.toggleAttribute('data-complete', total > 0 && done >= total);
        this.element.setAttribute('aria-label', `${done} of ${total} done`);
        if (this.hasCountTarget) this.countTarget.textContent = `${done}/${total}`;
    }

    _storageKey() {
        return this.memoryValue ? `chippie_onboard_ring_${this.memoryValue}` : null;
    }

    _recall() {
        const key = this._storageKey();
        if (!key) return null;
        try {
            const stored = localStorage.getItem(key);
            return stored === null ? null : Number(stored);
        } catch {
            return null;
        }
    }

    _remember() {
        const key = this._storageKey();
        if (!key) return;
        try {
            localStorage.setItem(key, String(this.doneValue));
        } catch {
            // Storage unavailable: the ring shows its value without the fill.
        }
    }
}
